import React, { useState } from 'react';
import Drawer, { FooterSpacer } from '../Drawer';
import Button from '../Button';
import { toCSV, csvMoney, reportCellText } from '../../domain/reportRunner';
import { REPORTS_WORDING as W, reportResultMeta, reportFilename } from '../../domain/reports';
import { useLocale } from '../../context/LocaleContext';
import { downloadDocumentPdf } from '../../data/documentLinks';
import s from './reports.module.css';

// A finished report: its parameters restated, then the table.

// The line under the title is `reportResultMeta` in src/domain/reports.js.

function saveBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

// ── Download PDF (#621) ─────────────────────────────────────────────────────
//
// The PDF is the server's: `documentLink` (data/documentLinks.js, handed in by
// ReportsPage) renders it from this report's stored rows — the rows the client
// folded, unchanged — with the studio's name, logo, accent and locale read on
// the server, keeps it, and answers with a link that asks for the bytes as an
// attachment. Following it saves the file under the name the server stored it
// with and leaves this page where it is. The phones share the same file.
export default function ReportResultDrawer({ report, onClose, documentLink = null }) {
  const { currency, formatMoney, datePattern, decimalSeparator } = useLocale();
  const [pdfBusy, setPdfBusy] = useState(false);
  const [pdfError, setPdfError] = useState(null);

  if (!report) return null;

  const cols = report.result?.columns || [];
  const rows = report.result?.rows || [];

  const download = () => {
    // The viewer sandbox blocks page-initiated downloads, so this is a no-op
    // there; in the app proper it hands the teacher the CSV their accountant
    // asked for.
    const csv = toCSV(report.typeId, cols, rows, decimalSeparator, csvMoney({ currency, formatMoney }));
    const blob = new Blob([csv], { type: 'text/csv' });
    saveBlob(blob, reportFilename(report.label, report.createdOn, 'csv'));
  };

  const downloadPdf = () => {
    setPdfBusy(true);
    setPdfError(null);
    downloadDocumentPdf(documentLink, 'report', report.id).then(
      () => setPdfBusy(false),
      (err) => {
        setPdfError(err?.message || W.pdfFailed);
        setPdfBusy(false);
      },
    );
  };

  return (
    <Drawer
      open
      onClose={onClose}
      title={report.label}
      width={860}
      footer={(
        <>
          <Button variant="secondary" onClick={onClose}>{W.close}</Button>
          <FooterSpacer />
          {rows.length && documentLink ? (
            <Button variant="secondary" onClick={downloadPdf} loading={pdfBusy}>{W.downloadPdf}</Button>
          ) : null}
          {rows.length ? <Button variant="secondary" onClick={download}>{W.downloadCsv}</Button> : null}
        </>
      )}
    >
      <div className={s.resultMeta}>{reportResultMeta(report, rows.length, datePattern)}</div>

      {pdfError ? <div className={s.resultNote}>{pdfError}</div> : null}

      {report.status === 'unsupported' ? (
        <div className={s.resultNote}>{report.reason}</div>
      ) : rows.length === 0 ? (
        <div className={s.resultEmpty}>{W.noRows}</div>
      ) : (
        <div className={s.resultWrap}>
          <table className={s.table}>
            <thead><tr>{cols.map((c) => <th key={c}>{c}</th>)}</tr></thead>
            <tbody>
              {rows.map((r, i) => (
                // Money in the studio's money (#502): the stored figure is the
                // CSV's `62.40`, bare on a screen that writes `$62.40`.
                <tr key={i}>
                  {cols.map((c) => (
                    <td key={c}>
                      {reportCellText(report.typeId, c, r[c], { currency, formatMoney, decimal: decimalSeparator })}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Drawer>
  );
}
